import { FunnelTypes } from 'features/co-pilot/co-pilot.types';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

export type RecruitingMode = 'internal' | 'external';

interface PipelineStore {
  /* high-level selection */
  selectedFunnelId: string | null;
  funnelType: FunnelTypes | null;
  recruitingMode: RecruitingMode;
  searchTerm: string;

  /* board */
  contacts: any[]; // raw contacts
  columns: Record<string, any[]>; // columnId → contacts
  loadingBoard: boolean;
  movingCard: boolean;
  prevColumnsSnapshot: Record<string, any[]> | null;

  /* actions */
  setSelectedFunnel: (id: string | null, type: FunnelTypes | null) => void;
  setRecruitingMode: (mode: RecruitingMode) => void;
  setSearchTerm: (term: string) => void;

  initialiseBoard: (contacts: any[], columns: Record<string, any[]>) => void;
  startMove: (snapshot: Record<string, any[]>) => void;
  confirmMove: (columns: Record<string, any[]>) => void;
  rollbackMove: () => void;
}

const storedRecruitingMode = (localStorage.getItem('recruiting-pipeline-mode') as RecruitingMode) || 'internal';

const usePipelineStore = createWithEqualityFn<PipelineStore>(
  (set) => ({
    selectedFunnelId: null,
    funnelType: null,
    recruitingMode: storedRecruitingMode,
    searchTerm: '',

    /* board */
    contacts: [],
    columns: {},
    loadingBoard: true,
    movingCard: false,
    prevColumnsSnapshot: null,

    /* actions */
    setSelectedFunnel: (id, type) => set(() => ({ selectedFunnelId: id, funnelType: type })),
    setRecruitingMode: (mode) => {
      localStorage.setItem('recruiting-pipeline-mode', mode);
      set(() => ({ recruitingMode: mode }));
    },
    setSearchTerm: (term) => set(() => ({ searchTerm: term })),

    initialiseBoard: (contacts, columns) =>
      set(() => ({ contacts, columns, loadingBoard: false })),

    startMove: (snapshot) =>
      set(() => ({ prevColumnsSnapshot: snapshot, movingCard: true })),

    confirmMove: (columns) =>
      set(() => ({ columns, movingCard: false, prevColumnsSnapshot: null })),

    rollbackMove: () =>
      set((state) => ({
        columns: state.prevColumnsSnapshot || state.columns,
        movingCard: false,
        prevColumnsSnapshot: null,
      })),
  }),
  shallow
);

export default usePipelineStore;